/**
 * Session persistence for user-uploaded wardrobe items.
 * Uses sessionStorage so items survive page refreshes within the same tab
 * but are cleared when the browser tab closes.
 *
 * Only items with CDN URLs (http/https) are persisted — blob: URLs are
 * ephemeral and wouldn't load after a refresh.
 */

import type { WardrobeItem, WardrobeCategoryData } from '@/types';

const STORAGE_KEY = 'ai-wardrobe-user-items';
const LOG = '[UserItemsStorage]';

interface PersistedItem {
  id: string;
  name: string;
  category: string;
  thumbnailUrl: string;
  imageUrl: string;
}

function isPersistedUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://');
}

function toPersistedItem(item: WardrobeItem): PersistedItem | null {
  if (!isPersistedUrl(item.imageUrl)) return null;
  return {
    id: item.id,
    name: item.name,
    category: item.category,
    thumbnailUrl: isPersistedUrl(item.thumbnailUrl) ? item.thumbnailUrl : item.imageUrl,
    imageUrl: item.imageUrl,
  };
}

function toWardrobeItem(p: PersistedItem): WardrobeItem {
  return {
    id: p.id,
    name: p.name,
    category: p.category as WardrobeItem['category'],
    thumbnailUrl: p.thumbnailUrl,
    imageUrl: p.imageUrl,
    isUserUploaded: true,
    isProcessing: false,
  };
}

/**
 * Save all user-uploaded items from the current categories to sessionStorage.
 */
export function persistUserItems(categories: WardrobeCategoryData[]): void {
  try {
    const items: PersistedItem[] = [];
    for (const cat of categories) {
      for (const item of cat.items) {
        if (!item.isUserUploaded) continue;
        const persisted = toPersistedItem(item);
        if (persisted) items.push(persisted);
      }
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (err) {
    console.warn(`${LOG} Failed to save:`, err);
  }
}

/**
 * Load persisted user items from sessionStorage.
 */
export function loadPersistedUserItems(): WardrobeItem[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: PersistedItem[] = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const items = parsed.map(toWardrobeItem);
    return items;
  } catch (err) {
    console.warn(`${LOG} Failed to load:`, err);
    return [];
  }
}

/**
 * Merge persisted user items into preset categories.
 * User items are prepended so they sit next to the "+ add" button at the top
 * of each category grid. Duplicates (by id) are skipped.
 */
export function mergeUserItemsIntoCategories(categories: WardrobeCategoryData[], userItems: WardrobeItem[]): WardrobeCategoryData[] {
  if (userItems.length === 0) return categories;

  const byCategory = new Map<string, WardrobeItem[]>();
  for (const item of userItems) {
    const list = byCategory.get(item.category) ?? [];
    list.push(item);
    byCategory.set(item.category, list);
  }

  return categories.map(cat => {
    const extras = byCategory.get(cat.id);
    if (!extras) return cat;

    const existingIds = new Set(cat.items.map(i => i.id));
    const newItems = extras.filter(i => !existingIds.has(i.id));
    if (newItems.length === 0) return cat;

    return { ...cat, items: [...newItems, ...cat.items] };
  });
}

/**
 * Clear persisted user items.
 */
export function clearPersistedUserItems(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
